import axios from 'axios';

// Function to get the backend URL dynamically
// Priority: runtime config > env var > same-origin (empty string for relative URLs)
export const getBackendUrl = () => {
  // Check runtime config first
  if (window.RUNTIME_CONFIG?.BACKEND_URL !== undefined) {
    return window.RUNTIME_CONFIG.BACKEND_URL; // Can be empty string for same-origin
  }
  // Fallback to env var
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  // Default: same-origin (relative URLs)
  return '';
};

// Create axios instance — cookies sent automatically for auth
const api = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send HttpOnly cookies with every request
});

// Add request interceptor to dynamically set baseURL
api.interceptors.request.use(
  (config) => {
    const backendUrl = getBackendUrl();
    config.baseURL = backendUrl ? `${backendUrl}/api` : '/api';

    // Access token from cookie is sent automatically by the browser.
    // As a fallback, also check localStorage for backward compatibility
    // during the migration period. This can be removed once all users
    // have re-authenticated with the cookie-based flow.
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // App-type hint for attribution snapshots: 'pwa' when running as an
    // installed PWA (standalone display-mode), otherwise 'web'.
    try {
      const isStandalone =
        (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
        window.navigator.standalone === true;
      config.headers['X-App-Type'] = isStandalone ? 'pwa' : 'web';
    } catch (e) {
      config.headers['X-App-Type'] = 'web';
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Track whether a token refresh is already in progress to prevent race conditions
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Handle token expiration with race condition protection
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Another refresh is in progress — queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Refresh token is sent automatically via HttpOnly cookie.
        // Also try localStorage as fallback during migration.
        const refreshToken = localStorage.getItem('refresh_token');
        const backendUrl = getBackendUrl();
        const refreshUrl = backendUrl ? `${backendUrl}/api/auth/refresh` : '/api/auth/refresh';
        const response = await axios.post(refreshUrl, null, {
          params: { refresh_token: refreshToken || '' },
          withCredentials: true,
        });

        // Store tokens in localStorage as fallback during migration
        if (response.data.access_token) {
          localStorage.setItem('access_token', response.data.access_token);
        }
        if (response.data.refresh_token) {
          localStorage.setItem('refresh_token', response.data.refresh_token);
        }

        processQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/auth';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  acceptInvitation: (token) => api.post('/auth/accept-invitation', null, { params: { token } }),
  setup2FA: () => api.post('/auth/2fa/setup'),
  verify2FA: (data) => api.post('/auth/2fa/verify', data),
  disable2FA: (data) => api.post('/auth/2fa/disable', data),
  resendVerification: (email) => api.post('/auth/resend-verification', null, { params: { email } }),
  forgotPassword: (email) => api.post('/auth/forgot-password', null, { params: { email } }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', null, { params: { token, new_password: newPassword } }),
  changePassword: (currentPassword, newPassword) => api.post('/auth/change-password', null, { params: { current_password: currentPassword, new_password: newPassword } }),
  updateProfile: (email, phone) => api.put('/auth/update-profile', { email, phone }),
  logout: () => api.post('/auth/logout'),
};

// Users API
export const usersAPI = {
  getUsers: () => api.get('/users/'),
  getUser: (id) => api.get(`/users/${id}/`),
  connectCoParent: (id) => api.post(`/users/connect/${id}/`),
};

// Calendar API
export const calendarAPI = {
  getEvents: () => api.get('/calendar/'),
  getEvent: (id) => api.get(`/calendar/${id}/`),
  createEvent: (data) => api.post('/calendar/', data),
  updateEvent: (id, data) => api.put(`/calendar/${id}/`, data),
  deleteEvent: (id) => api.delete(`/calendar/${id}/`),
  // Rotation templates (Prompt 4)
  getRotationTemplates: () => api.get('/calendar/rotation/templates'),
  previewRotation: (data) => api.post('/calendar/rotation/preview', data),
  applyRotation: (data) => api.post('/calendar/rotation/apply', data),
  // Parent calendar colors
  getColors: () => api.get('/calendar/color'),
  setColor: (color) => api.put('/calendar/color', { color }),
  // External ICS subscriptions
  getSubscriptions: () => api.get('/calendar/subscriptions'),
  addSubscription: (data) => api.post('/calendar/subscriptions', data),
  deleteSubscription: (id) => api.delete(`/calendar/subscriptions/${id}`),
  syncSubscription: (id) => api.post(`/calendar/subscriptions/${id}/sync`),
  // Photo-to-event (Claude vision) — returns proposals, never auto-creates
  photoExtract: (formData) => api.post('/calendar/photo-extract', formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// Messages API
export const messagesAPI = {
  getMessages: (receiverId = null) => api.get('/messages/', { params: { receiver_id: receiverId } }),
  sendMessage: (data) => api.post('/messages/', data),
  markAsRead: (id) => api.put(`/messages/${id}/read/`),
  getUnreadCount: () => api.get('/messages/unread/count/'),
  analyzeTone: (content) => api.post('/messages/analyze-tone', { content }),
  // Drafts (editable until sent)
  listDrafts: () => api.get('/messages/drafts'),
  createDraft: (data) => api.post('/messages/drafts', data),
  updateDraft: (id, data) => api.put(`/messages/drafts/${id}`, data),
  deleteDraft: (id) => api.delete(`/messages/drafts/${id}`),
  sendDraft: (id) => api.post(`/messages/drafts/${id}/send`),
};

// Reports API (Court-Admissible Records Layer)
export const reportsAPI = {
  getTypes: () => api.get('/reports/types'),
  getHistory: () => api.get('/reports/history'),
  // Returns a PDF blob for download
  generate: (data) => api.post('/reports/generate', data, { responseType: 'blob' }),
};

// Records integrity API (hash-chain verification)
export const recordsAPI = {
  verifyChain: (caseFileId = null) =>
    api.get('/records/verify-chain', { params: { case_file_id: caseFileId } }),
};

// Active Sessions API (session attribution & anti-impersonation)
export const sessionsAPI = {
  list: () => api.get('/sessions/'),
  revoke: (sessionId) => api.post(`/sessions/${sessionId}/revoke`),
  revokeAll: (keepCurrent = true) =>
    api.post('/sessions/revoke-all', null, { params: { keep_current: keepCurrent } }),
};

// Schedule Change Requests API
export const scheduleChangesAPI = {
  list: () => api.get('/schedule-changes/'),
  get: (id) => api.get(`/schedule-changes/${id}`),
  create: (data) => api.post('/schedule-changes/', data),
  respond: (id, data) => api.post(`/schedule-changes/${id}/respond`, data),
  withdraw: (id) => api.post(`/schedule-changes/${id}/withdraw`),
};

// Call Documentation & Safety API
export const callsAPI = {
  getConsent: () => api.get('/calls/consent'),
  updateConsent: (data) => api.put('/calls/consent', data),
  canCall: (callType) => api.get(`/calls/can-call/${callType}`),
  initiate: (data) => api.post('/calls/initiate', data),
  answer: (id) => api.post(`/calls/${id}/answer`),
  decline: (id) => api.post(`/calls/${id}/decline`),
  logEvent: (id, data) => api.post(`/calls/${id}/event`, data),
  end: (id) => api.post(`/calls/${id}/end`),
  requestRecording: (id) => api.post(`/calls/${id}/recording/request`),
  recordingConsent: (id, accept) => api.post(`/calls/${id}/recording/consent`, { accept }),
  transcribe: (id) => api.post(`/calls/${id}/transcribe`),
  searchTranscripts: (q, caseFileId = null) =>
    api.get('/calls/transcripts/search', { params: { q, case_file_id: caseFileId } }),
  list: () => api.get('/calls/'),
  get: (id) => api.get(`/calls/${id}`),
};

// In-app Notifications API
export const notificationsAPI = {
  list: (limit = 50) => api.get('/notifications/', { params: { limit } }),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
  getSettings: () => api.get('/notifications/settings'),
  updateSettings: (data) => api.post('/notifications/settings', data),
};

// Expenses API
export const expensesAPI = {
  getExpenses: () => api.get('/expenses/'),
  getExpense: (id) => api.get(`/expenses/${id}/`),
  createExpense: (data) => api.post('/expenses/', data),
  updateStatus: (id, status) => api.put(`/expenses/${id}/status/`, null, { params: { status_value: status } }),
  deleteExpense: (id) => api.delete(`/expenses/${id}/`),
  getSummary: () => api.get('/expenses/summary/total/'),
  // Reimbursement workflow (Prompt 5)
  getBalance: () => api.get('/expenses/balance'),
  getSplitRule: (caseFileId = null) => api.get('/expenses/split-rule', { params: { case_file_id: caseFileId } }),
  setSplitRule: (data) => api.put('/expenses/split-rule', data),
  approve: (id) => api.post(`/expenses/${id}/approve`),
  dispute: (id, reason) => api.post(`/expenses/${id}/dispute`, { reason }),
  pay: (id, data) => api.post(`/expenses/${id}/pay`, data || {}),
  remind: (id) => api.post(`/expenses/${id}/remind`),
  getEvents: (id) => api.get(`/expenses/${id}/events`),
  overdue: () => api.get('/expenses/reminders/overdue'),
  receiptExtract: (formData) => api.post('/expenses/receipt-extract', formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// Invoices API
export const invoiceAPI = {
  listInvoices: () => api.get('/invoices/'),
  createInvoice: (data) => api.post('/invoices/', data),
  payInvoice: (id) => api.post(`/invoices/${id}/pay`, {}),
  rejectInvoice: (id) => api.post(`/invoices/${id}/reject`, {}),
};

// Documents API
export const documentsAPI = {
  getDocuments: () => api.get('/documents/'),
  getDocument: (id) => api.get(`/documents/${id}/`),
  uploadDocument: (formData) => api.post('/documents/', formData, {
    headers: { 'Content-Type': undefined },  // Let browser set multipart boundary
  }),
  deleteDocument: (id) => api.delete(`/documents/${id}/`),
};

// Children API
export const childrenAPI = {
  getChildren: () => api.get('/children/'),
  getChild: (id) => api.get(`/children/${id}/`),
  addChild: (data) => api.post('/children/', data),
  updateChild: (id, data) => api.put(`/children/${id}/`, data),
  deleteChild: (id) => api.delete(`/children/${id}/`),
};

// Info Bank API (Prompt 6)
export const infoBankAPI = {
  categories: () => api.get('/info-bank/categories'),
  list: (childId, category = null) =>
    api.get('/info-bank/', { params: { child_id: childId, category } }),
  create: (data) => api.post('/info-bank/', data),
  update: (id, data) => api.put(`/info-bank/${id}`, data),
  remove: (id) => api.delete(`/info-bank/${id}`),
  history: (id) => api.get(`/info-bank/${id}/history`),
  emergencyCard: (childId) => api.get('/info-bank/emergency-card', { params: { child_id: childId } }),
};

// Locations API
export const locationsAPI = {
  getLocations: (childId = null) => api.get('/locations/', { params: { child_id: childId } }),
  addLocation: (data) => api.post('/locations/', data),
  getSharedLocations: () => api.get('/locations/shared/'),
  deleteLocation: (id) => api.delete(`/locations/${id}/`),
};

// Professionals API
export const professionalsAPI = {
  getProfessionals: (profession = null) => api.get('/professionals/', { params: { profession } }),
  getProfessional: (id) => api.get(`/professionals/${id}/`),
  addProfessional: (data) => api.post('/professionals/', data),
  deleteProfessional: (id) => api.delete(`/professionals/${id}/`),
};

// Parenting Plans API
export const parentingPlansAPI = {
  getPlans: () => api.get('/parenting-plans/'),
  getPlan: (id) => api.get(`/parenting-plans/${id}/`),
  createPlan: (data) => api.post('/parenting-plans/', data),
  updatePlan: (id, data) => api.put(`/parenting-plans/${id}/`, data),
  updateStatus: (id, status) => api.put(`/parenting-plans/${id}/status/`, null, { params: { status_value: status } }),
  deletePlan: (id) => api.delete(`/parenting-plans/${id}/`),
};

// Third Party API
export const thirdPartyAPI = {
  getThirdParties: () => api.get('/third-party/'),
  inviteThirdParty: (data) => api.post('/third-party/', data),
  updateStatus: (id, status) => api.put(`/third-party/${id}/status/`, null, { params: { status_value: status } }),
  deleteThirdParty: (id) => api.delete(`/third-party/${id}/`),
};

// Legal API
export const legalAPI = {
  acceptWaiver: (data) => api.post('/legal/waiver/accept/', data),
  checkWaiverStatus: () => api.get('/legal/waiver/status/'),
  sendContactMessage: (data) => api.post('/legal/contact/', data),
};

// SMS API
export const smsAPI = {
  sendMessage: (data) => api.post('/sms/send/', data),
  getMessages: () => api.get('/sms/'),
  scheduleReminder: (data) => api.post('/sms/reminder/', data),
  getReminders: () => api.get('/sms/reminders/'),
};

// Video API
export const videoAPI = {
  getToken: (roomName) => api.post('/video/token', { room_name: roomName }),
  createRoom: (roomName, maxParticipants = 2) => api.post('/video/rooms', { room_name: roomName, max_participants: maxParticipants }),
  getRoomInfo: (roomName) => api.get(`/video/rooms/${roomName}`),
  endRoom: (roomName) => api.delete(`/video/rooms/${roomName}`),
  getActiveRooms: () => api.get('/video/active-rooms'),
  sendCallInvitation: (data) => api.post('/video/send-call-invitation', data),
  startWaiting: (roomName) => api.post('/video/start-waiting', { room_name: roomName }),
  checkCoParentWaiting: () => api.get('/video/co-parent-waiting'),
  stopWaiting: () => api.delete('/video/stop-waiting'),
};

// Attorney/Case File API
export const caseFileAPI = {
  createCase: (data) => api.post('/attorney/cases', data),
  getCases: () => api.get('/attorney/cases'),
  getCase: (caseId) => api.get(`/attorney/cases/${caseId}`),
  updateCase: (caseId, data) => api.put(`/attorney/cases/${caseId}`, data),
  deleteCase: (caseId) => api.delete(`/attorney/cases/${caseId}`),
  addMember: (caseId, data) => api.post(`/attorney/cases/${caseId}/members`, data),
  removeMember: (caseId, parentId) => api.delete(`/attorney/cases/${caseId}/members/${parentId}`),
  inviteParent: (data) => api.post('/attorney/invite-parent', data),
  getMyParents: () => api.get('/attorney/my-parents'),
  getPendingInvitations: () => api.get('/attorney/pending-invitations'),
  getCaseActivity: (caseId) => api.get(`/attorney/cases/${caseId}/activity`),
  logCaseActivity: (caseId, data) => api.post(`/attorney/cases/${caseId}/activity`, data),
};


// GPS Check-in API
export const checkInAPI = {
  createCheckIn: (data) => api.post('/checkin/checkin', data),
  getUserCheckIns: (eventId = null) => api.get('/checkin/checkins', { params: { event_id: eventId } }),
  getEventCheckIns: (eventId) => api.get(`/checkin/checkins/event/${eventId}`),
  getSharedCheckIns: () => api.get('/checkin/checkins/shared'),
  getCheckInStats: () => api.get('/checkin/checkins/stats'),
};

// Moments API (Prompt 7) — photo sharing, no comments/reactions by design
export const momentsAPI = {
  list: () => api.get('/moments/'),
  post: (formData) => api.post('/moments/', formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }),
  remove: (id) => api.delete(`/moments/${id}`),
};

// Clinical Score API
export const clinicalScoreAPI = {
  getMyScore: (days = 30) => api.get('/clinical/score', { params: { days } }),
  getUserScore: (userId, days = 30) => api.get(`/clinical/score/${userId}`, { params: { days } }),
  getComparison: (days = 30) => api.get('/clinical/comparison', { params: { days } }),
  getMethodology: () => api.get('/clinical/methodology'),
  getCitations: () => api.get('/clinical/citations'),
};


// ============================================================
// Discrepancy Engine API (PROFESSIONAL DASHBOARD USE ONLY)
// Parents never see discrepancy scores or flags.
// ============================================================

// Parent-facing: submit Co-Parenting Index assessment
export const coParentingAssessmentAPI = {
  submitAssessment: (data) => api.post('/discrepancy/assessment', data),
  getMyAssessments: (caseId) => api.get('/discrepancy/assessment/my', { params: { case_id: caseId } }),
};

// Professional-facing: discrepancy data, flags, engagement ledger
export const discrepancyAPI = {
  // Discrepancy records per case (optionally filtered by period)
  getDiscrepancy: (caseId, period = null) =>
    api.get(`/discrepancy/discrepancy/${caseId}`, period ? { params: { assessment_period: period } } : {}),

  // Force-recompute discrepancy (after data corrections)
  recomputeDiscrepancy: (caseId, period) =>
    api.post(`/discrepancy/discrepancy/${caseId}/recompute`, null, { params: { assessment_period: period } }),

  // Active professional-dashboard flags (high_discrepancy, correlated_risk, etc.)
  getCaseFlags: (caseId) => api.get(`/discrepancy/flags/${caseId}`),
  dismissFlag: (caseId, flagType) => api.delete(`/discrepancy/flags/${caseId}/${flagType}`),

  // Engagement ledger events
  getEngagementLedger: (caseId, eventType = null, limit = 100) =>
    api.get(`/discrepancy/engagement-ledger/${caseId}`, {
      params: { event_type: eventType, limit },
    }),

  // Full professional case summary (discrepancy + flags + raw assessments + ledger)
  getCaseSummary: (caseId) => api.get(`/discrepancy/professional/case-summary/${caseId}`),
};


// ============================================================
// RTM Compliance Engine API (PROFESSIONAL DASHBOARD USE ONLY)
// Parent-facing UI is unchanged. Parents never see RTM data.
// ============================================================

// Clinician time logging (contributes to the 20-min RTM criterion)
export const rtmClinicianTimeAPI = {
  logTime: (data) => api.post('/rtm/clinician-time', data),
  getLogs: (caseId) => api.get(`/rtm/clinician-time/${caseId}`),
};

// RTM Status panel (professional dashboard per-case panel)
export const rtmAPI = {
  // Current period status — powers the RTM Status panel
  // Returns: qualifying_days, clinician_review, eligible, blocking_reason, cpt_code
  getStatus: (caseId) => api.get(`/rtm/status/${caseId}`),

  // On-demand (re)evaluation of a single case
  evaluate: (caseId) => api.post(`/rtm/evaluate/${caseId}`),

  // Generate billing summary PDF (GATED — returns 422 if not eligible)
  // Only callable (and button only enabled) when eligible = true
  generateBillingSummary: (caseId, providerNpi) =>
    api.post(`/rtm/billing-summary/${caseId}`, null, {
      params: { provider_npi: providerNpi },
      responseType: 'blob',   // receive PDF binary
    }),

  // Nightly batch evaluation (admin/scheduler trigger)
  runNightly: () => api.post('/rtm/run-nightly'),

  // Historical eligibility records for a case
  getHistory: (caseId) => api.get(`/rtm/history/${caseId}`),
};

// Subscription tiers & entitlements (Prompt 8)
export const stripeAPI = {
  getTiers: () => api.get('/stripe/tiers'),
  getEntitlements: () => api.get('/stripe/entitlements'),
  checkout: (packageId) => api.post('/stripe/checkout', {
    package_id: packageId,
    origin_url: window.location.origin,
  }),
  getStatus: (sessionId) => api.get(`/stripe/status/${sessionId}`),
};

// Waiver (versioned) (Prompt 8)
export const waiverAPI = {
  status: () => api.get('/legal/waiver/status'),
  accept: (fullName) => api.post('/legal/waiver/accept', { full_name: fullName }),
};

// MyCase — consolidated case view + packet (Prompt 10)
export const myCaseAPI = {
  overview: () => api.get('/mycase/overview'),
  timeline: (types = null) => api.get('/mycase/timeline', { params: { types } }),
  packet: (data) => api.post('/mycase/packet', data, { responseType: 'blob' }),
};
